import { useCallback, useEffect, useState } from 'react';
import { api, type Company, type NewSuspension, type SuspensionView } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { date as formatDate } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';
import type { Panel } from './Shell';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Zawieszenie wykonywania działalności (art. 22–25 ustawy Prawo przedsiębiorców).
 *
 * Stoi w danych firmy, a nie w ustawieniach podatkowych, bo to **fakt z CEIDG**, nie wybór sposobu
 * rozliczenia: firma zawieszona ma dalej ten sam NIP, tę samą formę opodatkowania i te same
 * niezapłacone faktury. Dlatego trzymamy okresy, a nie przełącznik „zawieszona".
 *
 * Panel mówi wprost, co z okresu wynika — składki, terminy, amortyzacja i Mały ZUS Plus — bo to
 * cztery różne rachunki i nikt nie zgadnie z samej daty, że zmieniły się wszystkie.
 */
export function SuspensionsPanel({
  company,
  t,
  locale,
  editable,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  editable: boolean;
  onGo?: (panel: Panel) => void;
}) {
  const [rows, setRows] = useState<SuspensionView[] | null>(null);
  const [form, setForm] = useState<NewSuspension | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api.suspensions(company.id));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <header>
        <h4>{t.suspensions.title}</h4>
        <div className="sp">
          {editable && !form && (
            <button
              type="button"
              className="btn sm"
              disabled={busy}
              onClick={() => setForm({ startsOn: today(), endsOn: '', note: '' })}
            >
              <Icon.Plus />
              {t.suspensions.add}
            </button>
          )}
        </div>
      </header>
      <p className="sm muted">{t.suspensions.lead}</p>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      {form && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.addSuspension(company.id, {
                startsOn: form.startsOn,
                endsOn: form.endsOn?.trim() || null,
                note: form.note?.trim() || null,
              });
              setForm(null);
            });
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="suFrom">{t.suspensions.startsOn}</label>
              <input
                id="suFrom"
                className="inp"
                type="date"
                value={form.startsOn}
                onChange={(event) => setForm({ ...form, startsOn: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="suTo">{t.suspensions.endsOn}</label>
              <input
                id="suTo"
                className="inp"
                type="date"
                value={form.endsOn ?? ''}
                onChange={(event) => setForm({ ...form, endsOn: event.target.value })}
              />
              <p className="hint">{t.suspensions.endsOnHint}</p>
            </div>
            <div className="field">
              <label htmlFor="suNote">{t.suspensions.note}</label>
              <input
                id="suNote"
                className="inp"
                value={form.note ?? ''}
                onChange={(event) => setForm({ ...form, note: event.target.value })}
              />
              <p className="hint">{t.suspensions.noteHint}</p>
            </div>
          </div>
          <div className="row">
            <button type="submit" className="btn pri" disabled={busy}>
              {t.suspensions.save}
            </button>
            <button type="button" className="btn ghost" onClick={() => setForm(null)}>
              {t.suspensions.cancel}
            </button>
          </div>
        </form>
      )}

      {rows?.length === 0 ? (
        <div className="empty">
          <Icon.Calendar />
          <b>{t.suspensions.none}</b>
          <p>{t.suspensions.noneHint}</p>
        </div>
      ) : (
        rows && (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.suspensions.startsOn}</th>
                  <th>{t.suspensions.endsOn}</th>
                  <th className="n">{t.suspensions.days}</th>
                  <th>{t.suspensions.note}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.suspension.id}>
                    <td className="doc">{formatDate(row.suspension.startsOn, locale)}</td>
                    <td className="doc">
                      {row.suspension.endsOn ? (
                        formatDate(row.suspension.endsOn, locale)
                      ) : (
                        <span className="chip st-warn">
                          <i />
                          {t.suspensions.open}
                        </span>
                      )}
                    </td>
                    <td className="n">
                      {row.days}
                      {/* Krótsze niż 30 dni nie jest skuteczne — najczęściej to pomyłka w dacie */}
                      {row.tooShort && (
                        <span className="chip st-warn">
                          <i />
                          {t.suspensions.tooShort}
                        </span>
                      )}
                    </td>
                    <td className="sm muted">{row.suspension.note ?? '—'}</td>
                    <td className="act">
                      <div className="row">
                        {editable && row.open && (
                          <button
                            type="button"
                            className="btn sm"
                            disabled={busy}
                            onClick={() => {
                              const lastDay = window.prompt(t.suspensions.resumePrompt, today());
                              if (lastDay) {
                                void run(() => api.resumeBusiness(company.id, row.suspension.id, lastDay));
                              }
                            }}
                          >
                            {t.suspensions.resume}
                          </button>
                        )}
                        {editable && (
                          <button
                            type="button"
                            className="btn danger ico sm"
                            aria-label={t.suspensions.remove}
                            title={t.suspensions.remove}
                            disabled={busy}
                            onClick={() => {
                              const reason = window.prompt(t.suspensions.removeReason);
                              if (reason) {
                                void run(() => api.removeSuspension(company.id, row.suspension.id, reason));
                              }
                            }}
                          >
                            <Icon.Trash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Co z zawieszenia wynika — cztery różne rachunki, więc mówimy to raz, przy liście */}
      {(rows?.length ?? 0) > 0 && <p className="note">{t.suspensions.effects}</p>}
    </div>
  );
}
